import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams, useLocation } from 'react-router';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Collapse,
  Grid,
  TextField,
  Slider,
  Typography,
  FormControl,
  Container,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton,
  InputLabel,
  InputAdornment,
  OutlinedInput,
  CircularProgress,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import html2canvas from 'html2canvas';
import AppTopBar from '../../app-bar/AppBar';
import FilterDropDown from '../../filter-dropdown/FilterDropDown';
import Footer from '../../footer/Footer';
import RefineRecordsModal from './RefineRecordsModal';
import HelpModal from './HelpModal';
import PdfReportModal from '../../pdf-report-modal/PdfReportModal';
import { PAGES } from '../../../constants/pages';
import { QLIK_APP_REF, INDUSTRY_FIELD, CLASS_DESCRIPTION_FIELD, DATE_RANGE_FIELD } from '../../../constants';
import type { RootState } from '../../../store/store';
import { store } from '../../../store/store';
import { setVariableReportFor, setVariableReportBy, selectQlikCalcVariableByName, selectQlikVariableByName } from '../../../store/slice/qlikVariableSlice';
import { getQlikResources } from '../../../services/getQlikResources';
import { getVariableContentByName, setVariableByName } from '../../../services/qlikVariableService';
import { applySelectMatch, applySelectPossible } from '../../../services/qlikSelectionService';
import { gtm } from '../../../services/gtm';
import type { ExtraControlsConfig } from '../../../types/extraControls';
import QlikKpi from '../../qlik-kpi/QlikKpi';
import SimpleQlikKpi from '../../simple-qlik-kpi/SimpleQlikKpi';

const RiskCalc: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const currentPath = location.pathname;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const page = useMemo(() => PAGES['cyber-risk-calculators'].contents ?? {}, []);
  const dispatch = store.dispatch;

  // State
  const [pageTitle] = useState(page.title?.text || '');
  const [pageSubtitle] = useState(page.sub?.text || '');
  const [legalText] = useState(page.legal || '');
  const [selectionIndustry, setSelectionIndustry] = useState<string>("")
  const [selectionIndustryClassName, setSelectionIndustryClassName] = useState<string>("")
  const [showEstimatedRecords, setShowEstimatedRecords] = useState(false);
  const [isRecCollapsed, setIsRecCollapsed] = useState(true);
  const [isCostCollapsed, setIsCostCollapsed] = useState(true);
  const [waitForUpdate, setWaitForUpdate] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [recVarsNames, setRecVarsNames] = useState<any[]>([]);
  const [isRefineModalOpen, setIsRefineModalOpen] = useState(false);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);  
  const [isRecordsHelpOpen, setIsRecordsHelpOpen] = useState(false);
  const [isBreachHelpOpen, setIsBreachHelpOpen] = useState(false);
  const [isPdfReportModalOpen, setIsPdfReportModalOpen] = useState(false);
  const [autoLaunchPdf, setAutoLaunchPdf] = useState(false);
  const [revenueInput, setRevenueInput] = useState('');
  const [recordCountInput, setRecordCountInput] = useState('');
  const [sliderValue, setSliderValue] = useState<number>(0);

  // Selectors
  const appSelections = useSelector((state: RootState) => state.qlikSelection.qlikAppSelections[QLIK_APP_REF]);
  const calcVariableName = 'calcVars';
  const qlikCalcVariables = useSelector((state: RootState) => selectQlikCalcVariableByName(state, calcVariableName));

  // Memoized calculation criteria check - requires Industry, Class, and Annual Revenue
  const calcCriteria = useMemo(() => {
    return !!(
      selectionIndustry && 
      selectionIndustryClassName && 
      qlikCalcVariables?.vQRevenue && 
      qlikCalcVariables?.vQRevenue > 0
    );
  }, [selectionIndustry, selectionIndustryClassName, qlikCalcVariables?.vQRevenue]);

  useEffect(() => {
    if (qlikCalcVariables?.vUserRecordCount) {
        setShowEstimatedRecords(false);
      } else {
        setShowEstimatedRecords(true);
      }
  }, [qlikCalcVariables?.vUserRecordCount]);
  
  useEffect(() => {
    const initialize = async () => {
      await loadVariables();
      await initFromUrlParams();
    };
    initialize();
  }, []);

  useEffect(() => {
    if(appSelections) {
      // const program = appSelections.find((field:any) => field.name === "Program");
      const industry = appSelections.filter((field:any) => field.qField === INDUSTRY_FIELD);
      setSelectionIndustry(industry?.[0]?.qSelected)
      const industryClassName = appSelections.filter((field:any) => field.qField === CLASS_DESCRIPTION_FIELD);
      setSelectionIndustryClassName(industryClassName?.[0]?.qSelected)
    }
  }, [appSelections])

    // Keep the revenue input field in sync when Qlik variable updates arrive
    useEffect(() => {
        if (qlikCalcVariables?.vQRevenue !== undefined && qlikCalcVariables?.vQRevenue !== null) {
            setRevenueInput(String(qlikCalcVariables.vQRevenue));
        } else {
            setRevenueInput('');
        }
    }, [qlikCalcVariables?.vQRevenue]);

    useEffect(() => {
        if (qlikCalcVariables?.vUserRecordCount !== undefined && qlikCalcVariables?.vUserRecordCount !== null) {
            setRecordCountInput(String(qlikCalcVariables.vUserRecordCount));
        } else {
            setRecordCountInput('');
        }
    }, [qlikCalcVariables?.vUserRecordCount]);

    useEffect(() => {
        if (qlikCalcVariables?.vBreachSeverity !== undefined && qlikCalcVariables?.vBreachSeverity !== null) {
            setSliderValue(qlikCalcVariables.vBreachSeverity);
        }
    }, [qlikCalcVariables?.vBreachSeverity]);

  const initFromUrlParams = async () => {
    try {
      const indClass = searchParams.get('indClass');
      const dateRange = searchParams.get('dateRange');
      const annualRev = searchParams.get('annualRev');
      const userRecords = searchParams.get('userRecords');
      const reportFor = searchParams.get('reportFor');
      const reportBy = searchParams.get('reportBy');
      const launchPdf = searchParams.get('launchPdf');

      if (indClass) {
        await applySelectMatch(QLIK_APP_REF, CLASS_DESCRIPTION_FIELD, indClass, '', currentPath);
        await applySelectPossible(QLIK_APP_REF, INDUSTRY_FIELD);
        gtm.formEvent(`Selected: Industry Class: ${indClass}`, 'Risk Calculators');
      }
      if (dateRange) {
        await applySelectMatch(QLIK_APP_REF, DATE_RANGE_FIELD, dateRange, "", currentPath);
      }
      if (annualRev) {
        await setVariableByName(calcVariableName, 'vQRevenue', parseInt(annualRev), QLIK_APP_REF, 'Risk Calculators');
      }
      if (userRecords) {
        await setVariableByName(calcVariableName,'vUserRecordCount', parseInt(userRecords), QLIK_APP_REF, 'Risk Calculators');
      }
      if (reportFor) {
        dispatch(setVariableReportFor(reportFor));
      }
      if (reportBy) {
        dispatch(setVariableReportBy(reportBy));
      }

      // Launch PDF modal if launchPdf param is present (marketplace users)
      if (launchPdf) {
        setAutoLaunchPdf(true);
        setIsPdfReportModalOpen(true);
      }

    //   updateInitialStep()

      setInitialized(true);
    } catch (error) {
      console.error('Error initializing from URL params:', error);
      setInitialized(true);
    }
  };

  const loadVariables = async () => {
    try {
      
      // Load variable names from config
      const varsConfig = await getQlikResources('qlik-variables');
      const recVarsNames1 = varsConfig?.recVarsNames1 || [];
      setRecVarsNames(recVarsNames1);

      // Load each variable's content
      for (const varObj of recVarsNames1) {
        const varName = Object.keys(varObj)[0];
        const varConfig = varObj[varName];
        await getVariableContentByName(calcVariableName, varName, varConfig.appRef);
      }
    } catch (error) {
      console.error('Error loading variables:', error);
    }
  };

  const updateInitialStep = async () => {
    try {
      await setVariableByName(calcVariableName, 'vQRevenue', qlikCalcVariables?.vQRevenue, QLIK_APP_REF, 'Risk Calculators');
      await setVariableByName(calcVariableName, 'vUserRecordCount', qlikCalcVariables?.vUserRecordCount, QLIK_APP_REF, 'Risk Calculators');
      
      setWaitForUpdate(false);
    //   gtm.formEvent('Update Calculation', 'Risk Calculators');
    } catch (error) {
      console.error('Error updating initial step:', error);
    }
  };

  const clearAllCalcs = () => {
    window.location.reload();
  };

    const commitRevenue = () => {
        const value = revenueInput === '' ? 0 : Number(revenueInput);
        setVariableByName(calcVariableName, 'vQRevenue', value, QLIK_APP_REF, 'Risk Calculators');
    };

    const commitRecordCount = () => {
        const value = recordCountInput === '' ? 0 : Number(recordCountInput);
        setVariableByName(calcVariableName, 'vUserRecordCount', value, QLIK_APP_REF, 'Risk Calculators');
    };

    const handleRevenueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRevenueInput(event.target.value);
        setWaitForUpdate(true);
    };

    const handleRecordCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRecordCountInput(event.target.value);
        setWaitForUpdate(true);
    };

    const handleRevenueKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            commitRevenue();
        }
    };

    const handleRecordCountKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            commitRecordCount();
        }
    };
//   const handleRevenueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const raw = event.target.value;
//     const value = raw === '' ? 0 : Number(raw);
//     setVariableByName(calcVariableName, 'vQRevenue', value, QLIK_APP_REF, 'Risk Calculators');
//   };

//   const handleRecordCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const raw = event.target.value;
//     const value = raw === '' ? 0 : Number(raw);
//     setVariableByName(calcVariableName, 'vUserRecordCount', value, QLIK_APP_REF, 'Risk Calculators');
//   };

  const handlePercB2BChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const value = raw === '' ? 0 : Number(raw);
    setVariableByName(calcVariableName, 'vC12PercB2B', value, QLIK_APP_REF, 'Risk Calculators');
  };

  const handleAvgB2BTransactionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const value = raw === '' ? 0 : Number(raw);
    setVariableByName(calcVariableName, 'vC14B2B', value, QLIK_APP_REF, 'Risk Calculators');
  };

  const handleAvgB2CTransactionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const value = raw === '' ? 0 : Number(raw);
    setVariableByName(calcVariableName, 'vC14IND', value, QLIK_APP_REF, 'Risk Calculators');
  };

  const handleRefineModalOpen = () => {
    setIsRefineModalOpen(true);
  };

  const handleRefineModalClose = () => {
    setIsRefineModalOpen(false);
  };

  const handleSeverityChange = (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    const value = typeof newValue === 'number' ? newValue : newValue[0];
    setSliderValue(value);
  };

  const handleSeverityCommit = (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    const value = typeof newValue === 'number' ? newValue : newValue[0];
    setVariableByName(calcVariableName, 'vBreachSeverity', value, QLIK_APP_REF, 'Risk Calculators');
  };

  function getAriaValueText(value: number) {
    return `${value}%`;
  }

  const toggleRecCollapse = () => {
    setIsRecCollapsed(!isRecCollapsed);
  };

  const toggleCostCollapse = () => {
    setIsCostCollapsed(!isCostCollapsed);
  };

  const screenshotThis = async (id: string) => {
    const element = document.getElementById(id);
    if (!element) {
      console.error(`Element with id "${id}" not found`);
      return;
    }

    const title = id === 'breachCalc' 
      ? 'Chubb Cyber Index - Breach Calculator.png' 
      : 'Chubb Cyber Index - Estimated Records Calculator.png';

    setIsCapturingScreenshot(true);
    
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: 'transparent',
        width: element.offsetWidth,
        height: element.offsetHeight,
      });
      
      saveCanvasAs(canvas, title);
    } catch (error) {
      console.error('Error capturing screenshot:', error);
    } finally {
      setIsCapturingScreenshot(false);
    }
  };

  const saveCanvasAs = (canvas: HTMLCanvasElement, fileName: string) => {
    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

    // Map slider value to colors (converted from Angular getSelectionBarColor/getPointerColor)
    const getSelectionBarColor = (value: number = 0): string => {
        if (value <= 33) return '#4caf50'; // green
        if (value <= 66) return '#ff9800'; // orange
        if (value <= 75) return '#ffeb3b'; // yellow
        return '#f44336'; // red
    };

    const getPointerColor = (value: number = 0): string => {
        if (value <= 33) return '#4caf50'; // green
        if (value <= 66) return '#ff9800'; // orange
        if (value <= 75) return '#ffeb3b'; // yellow
        return '#f44336'; // red
    };

    const getValueLabelTextColor = (hex: string): string => {
        // Ensure readable text over the value label background
        return hex.toLowerCase() === '#ffeb3b' ? '#000' : '#fff';
    };

  // Filter elements
  const filterElements = null; // Risk calc doesn't use the standard filter bar

  // Extra controls config
  const extraControls: ExtraControlsConfig = {
    startOver: { enable: true },
    lastUpdate: { enable: true, componentLocation: 'nav' },
  };

  return (
    <>
        <AppTopBar
          extraControls={extraControls}
          filterElements={filterElements}
        />

      
        {/* Title Section */}
        <Grid container
            component="main"
            sx={{
            py: 2,
            px: { xs: 2, sm: 2, md: 2 },
            // Add margin for the left drawer on desktop (md and up)
            // The drawer is 57px when collapsed, 257px when expanded
            ml: { xs: 0, md: '57px' }, // Collapsed drawer width
            transition: 'margin-left 0.3s'
            }}
        >
            <Grid size={{ xs: 12 }}>
                <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 2 }}>
                    <Typography
                        variant="h5"
                        sx={{ mb: 1, fontWeight: 400 }}
                    >
                        {pageTitle}
                    </Typography>
                    <Box sx={{ borderTop: 3, borderColor: 'primary.main', width: 60, mx: 'auto', mb: 2 }} />
                </Box>

                <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3 }}>
                    <Typography
                        variant="h5"
                        sx={{ mb: 2, fontWeight: 400 }}
                    >
                        {pageTitle}
                    </Typography>
                    <Box sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 2 }}>
                        <Typography
                            variant="body1"
                        >
                            {pageSubtitle}
                        </Typography>
                    </Box>
                </Box>
            </Grid>

            {/* Main Calculator Card */}
            <Grid size={{ xs: 12 }} >
                <Card
                    sx={{
                        borderTop: 3,
                        borderColor: 'primary.main',
                        borderRadius: 0,
                        position: 'relative',
                    }}
                >
                    <CardContent>
                    {/* Step 1 Header */}
                    {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}> */}
                        <Grid container spacing={2} sx={{ mb: 2, flexGrow: 1 }} >
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="h6" component="h4">
                                    Select Industry, Class and Annual Revenue
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                    variant="text"
                                    startIcon={<ClearIcon />}
                                    onClick={clearAllCalcs}
                                    sx={{ borderColor: 'grey.400', color: 'text.secondary', textTransform: 'none' }}
                                    >
                                    Clear All Fields
                                </Button>
                            </Grid>
                        </Grid>

                        {/* Initial Step Inputs */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <FilterDropDown
                                    formName="Risk Calculators"
                                    fieldName={INDUSTRY_FIELD}
                                    fieldLabel="Industry"
                                    qlikSort="qSortByAscii"
                                    multiSelect={false}
                                    required
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <FilterDropDown
                                    formName="Risk Calculators"
                                    fieldName={CLASS_DESCRIPTION_FIELD}
                                    fieldLabel="Industry Class"
                                    qlikSort="qSortByAscii"
                                    multiSelect={false}
                                    required
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Tooltip title="Your company's estimated annual revenue in dollars" placement="top">
                                    <FormControl fullWidth required>
                                        <InputLabel htmlFor="outlined-adornment-amount">Annual Revenue ($)</InputLabel>
                                        <OutlinedInput
                                            size='small'
                                            required
                                            fullWidth
                                            type='number'
                                            id="outlined-adornment-amount"
                                            startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                            color='primary'
                                            value={revenueInput}
                                            onChange={handleRevenueChange}
                                            onBlur={commitRevenue}
                                            onKeyUp={handleRevenueKeyUp}
                                            label={'Annual Revenue ($)'}
                                        />
                                    </FormControl>
                                </Tooltip>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Tooltip 
                                    title="If you know the number of records enter it here. Otherwise, leave the value at zero, click 'Update Calculation', and use the Estimated Record Calculator that will appear below."
                                    placement="top"
                                >
                                    <TextField
                                        fullWidth
                                        variant='outlined'
                                        type="number"
                                        size='small'
                                        label='Number of records (if known)'
                                        value={recordCountInput}
                                        onChange={handleRecordCountChange}
                                        onBlur={commitRecordCount}
                                        onKeyUp={handleRecordCountKeyUp}
                                    />
                                </Tooltip>
                            </Grid>
                        </Grid>

                        <Grid container>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2" color="error" sx={{ display: 'block', mb: 2 }}>
                                    * Required fields
                                </Typography>
                            </Grid>
                        </Grid>

                        <Grid container justifyContent="center">
                            <Grid size={{ xs: 12, sm: 'auto' }}>
                                <Button
                                    variant="contained"
                                    onClick={updateInitialStep}
                                    disabled={!calcCriteria}
                                    sx={{ mb: 3, textTransform: 'none' }}
                                >
                                    Update Calculation
                                </Button>
                            </Grid>
                        </Grid>

                        {/* Calculators Section */}
                        {(calcCriteria && !waitForUpdate) && (
                            <Box
                                sx={{
                                    // opacity: calcCriteria && !waitForUpdate ? 1 : 0,
                                    transition: 'opacity 0.3s',
                                    pointerEvents: calcCriteria && !waitForUpdate ? 'auto' : 'none',
                                }}
                            >
                                <Grid container spacing={3}>
                                    {/* Records Calculator */}
                                    {showEstimatedRecords && (
                                        <Grid size={{ xs: 12, md: 6 }} id='recordsCalc'>
                                            <Card variant="outlined">
                                                <CardHeader
                                                    sx={{py: 1}}
                                                    title={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="h6">
                                                                {page.records?.[0]?.title || 'Estimated Records Calculator'}
                                                            </Typography>
                                                            <IconButton size="small" aria-label="help" onClick={() => setIsRecordsHelpOpen(true)}>
                                                                <InfoIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => screenshotThis('recordsCalc')}
                                                                disabled={isCapturingScreenshot}
                                                            >
                                                                {isCapturingScreenshot ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <CameraAltIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Box>
                                                    }
                                                />
                                                {calcCriteria && (
                                                    <Box sx={{ px: 2, pb: 1 }}>
                                                        <Button
                                                            size="small"
                                                            onClick={toggleRecCollapse}
                                                            sx={{ textTransform: 'none', fontStyle: 'italic' }}
                                                        >
                                                            Refine Estimated Records
                                                        </Button>
                                                    </Box>
                                                )}
                                                <Collapse in={!isRecCollapsed && calcCriteria}>
                                                    <CardContent>
                                                        <Grid container spacing={2}>
                                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                                <Tooltip 
                                                                    title="% of sales to other businesses (B2B)" 
                                                                    placement="top"
                                                                >
                                                                    <TextField
                                                                        fullWidth
                                                                        variant='outlined'
                                                                        type="number"
                                                                        size='small'
                                                                        label='% Revenue B2B'
                                                                        value={qlikCalcVariables?.vC12PercB2B !== undefined ? qlikCalcVariables.vC12PercB2B : ''}
                                                                        onChange={handlePercB2BChange}
                                                                        slotProps={{
                                                                            input: {
                                                                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                            }
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </Grid>

                                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                                <Tooltip 
                                                                    title="Average size of a sale to businesses" 
                                                                    placement="top"
                                                                >
                                                                    <TextField
                                                                        fullWidth
                                                                        variant='outlined'
                                                                        type="number"
                                                                        size='small'
                                                                        label='Average B2B Transaction ($)'
                                                                        value={qlikCalcVariables?.vC14B2B !== undefined ? qlikCalcVariables.vC14B2B : ''}
                                                                        onChange={handleAvgB2BTransactionChange}
                                                                        slotProps={{
                                                                            input: {
                                                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                                            }
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </Grid>

                                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                                <Tooltip 
                                                                    title="Average size of a sale to individuals" 
                                                                    placement="top"
                                                                >
                                                                    <TextField
                                                                        fullWidth
                                                                        variant='outlined'
                                                                        type="number"
                                                                        size='small'
                                                                        label='Average B2C Transaction ($)'
                                                                        value={qlikCalcVariables?.vC14IND !== undefined ? qlikCalcVariables.vC14IND : ''}
                                                                        onChange={handleAvgB2CTransactionChange}
                                                                        slotProps={{
                                                                            input: {
                                                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                                            }
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </Grid>

                                                            <Grid size={{ xs: 12 }}>
                                                                <Button
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    startIcon={<TuneIcon />}
                                                                    onClick={handleRefineModalOpen}
                                                                    disabled={!(qlikCalcVariables?.vC12PercB2B || qlikCalcVariables?.vC14B2B || qlikCalcVariables?.vC14IND)}
                                                                    sx={{ textTransform: 'none', display: { xs: 'none', sm: 'flex' } }}
                                                                >
                                                                    Refine even further
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                    </CardContent>
                                                </Collapse>
                                                <CardContent sx={{py: 1}}>
                                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                                        <Grid size={{ xs: 12 }}>
                                                            <QlikKpi qlikVizName="risk-calc-tot-estimate-records" backColor="#6E27C5" />
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                                        <Grid size={{ xs: 12, sm: 6 }}>
                                                            <QlikKpi qlikVizName="risk-calc-ind-min" backColor="#4B4E53" short />
                                                        </Grid>
                                                        <Grid size={{ xs: 12, sm: 6 }}>
                                                            <QlikKpi qlikVizName="risk-calc-ind-max" backColor="#01C1D6" short />
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}

                                    {/* Breach Calculator */}
                                    <Grid size={{ xs: 12, md: showEstimatedRecords ? 6 : 12 }} id='breachCalc'>
                                        <Card variant="outlined">
                                            <CardHeader
                                                sx={{py: 1}}
                                                title={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="h6">
                                                        {page.breach?.[0]?.title || 'Estimated Incident Costs'}
                                                    </Typography>
                                                    <IconButton size="small" aria-label="help" onClick={() => setIsBreachHelpOpen(true)}>
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => screenshotThis('breachCalc')}
                                                        disabled={isCapturingScreenshot}
                                                    >
                                                        {isCapturingScreenshot ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            <CameraAltIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Box>
                                                }
                                            />
                                            {calcCriteria && (
                                                <Box sx={{ px: 2, pb: 1 }}>
                                                    <Button
                                                        size="small"
                                                        onClick={toggleCostCollapse}
                                                        sx={{ textTransform: 'none', fontStyle: 'italic' }}
                                                    >
                                                        Refine Number of Records Compromised
                                                    </Button>
                                                </Box>
                                            )}
                                            <Collapse in={!isCostCollapsed}>
                                                <CardContent sx={{mx: 2}}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        gutterBottom
                                                        // sx={{ color: getPointerColor(qlikCalcVariables?.vBreachSeverity ?? 0) }}
                                                    >
                                                        Breach Severity: {qlikCalcVariables?.vBreachSeverity}%
                                                    </Typography>
                                                    <Slider
                                                        value={sliderValue}
                                                        onChange={handleSeverityChange}
                                                        onChangeCommitted={handleSeverityCommit}
                                                        getAriaValueText={getAriaValueText}
                                                        min={0}
                                                        max={100}
                                                        marks={[
                                                            { value: 0, label: '0%' },
                                                            { value: 100, label: '100%' }
                                                        ]}
                                                        valueLabelDisplay="on"
                                                        valueLabelFormat={(value) => `${value}%`}
                                                        sx={{
                                                            '& .MuiSlider-rail': {
                                                                backgroundColor: '#bdbdbd',
                                                                opacity: 1,
                                                            },
                                                            '& .MuiSlider-track': {
                                                                backgroundColor: getSelectionBarColor(qlikCalcVariables?.vBreachSeverity ?? 0),
                                                                border: 'none',
                                                            },
                                                            '& .MuiSlider-thumb': {
                                                                backgroundColor: getPointerColor(qlikCalcVariables?.vBreachSeverity ?? 0),
                                                                border: `2px solid ${getPointerColor(qlikCalcVariables?.vBreachSeverity ?? 0)}`,
                                                                '&:hover, &.Mui-focusVisible': {
                                                                    boxShadow: `0 0 0 8px ${getPointerColor(qlikCalcVariables?.vBreachSeverity ?? 0)}33`,
                                                                },
                                                                '&.Mui-active': {
                                                                    boxShadow: `0 0 0 14px ${getPointerColor(qlikCalcVariables?.vBreachSeverity ?? 0)}33`,
                                                                },
                                                            },
                                                            '& .MuiSlider-valueLabel': {
                                                                backgroundColor: getPointerColor(qlikCalcVariables?.vBreachSeverity ?? 0),
                                                                color: getValueLabelTextColor(getPointerColor(qlikCalcVariables?.vBreachSeverity ?? 0)),
                                                            },
                                                        }}
                                                    />
                                                </CardContent>
                                            </Collapse>
                                            <CardContent sx={{py: 1}}>
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 12 }}>
                                                        <QlikKpi qlikVizName="risk-calc-tot-cyb-inc-costs" backColor="#150F96" />
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <SimpleQlikKpi qlikVizName="risk-calc-inc-investigation"/>
                                                        <SimpleQlikKpi qlikVizName="risk-calc-crisis-mgt"/>
                                                        <SimpleQlikKpi qlikVizName="risk-calc-pci"/>
                                                        <SimpleQlikKpi qlikVizName="risk-calc-fines-penalties"/>
                                                        <SimpleQlikKpi qlikVizName="risk-calc-ransomware"/>
                                                        <SimpleQlikKpi qlikVizName="risk-calc-data-restoration"/>
                                                        <SimpleQlikKpi qlikVizName="risk-calc-biz-interruption"/>
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <Box 
                                                            sx={{ 
                                                                display: 'flex', 
                                                                justifyContent: 'space-between', 
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Box>
                                                                <img 
                                                                    src="/assets/images/logos/chubb-grey.png" 
                                                                    alt="Chubb logo"
                                                                    style={{ maxWidth: '120px', height: 'auto' }}
                                                                />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    *In partnership with
                                                                </Typography>
                                                                <img 
                                                                    src="/assets/images/logos/netDLogo.png" 
                                                                    alt="Net Diligence logo"
                                                                    style={{ maxWidth: '100px', height: 'auto' }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Empty State */}
                        {!calcCriteria && (
                            <Box sx={{ textAlign: 'center', py: 4, backgroundColor: '#d9d9d9' }}>
                                <Typography color="text.secondary" fontStyle="italic">
                                    To estimate the number of records at risk for your organization, please provide your
                                    company's industry, industry class, and annual revenue in the fields above.
                                </Typography>
                            </Box>
                        )}
                    </CardContent>

                    {/* Legal Footer */}
                    <CardActions sx={{ backgroundColor: '#f5f5f5', p: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            {legalText}
                        </Typography>
                    </CardActions>
                </Card>
            </Grid>
        </Grid>

        <Footer componentLocation="dashboard" />

        <RefineRecordsModal 
            open={isRefineModalOpen} 
            onClose={handleRefineModalClose}
        />

        {/* Records Help Modal */}
        <HelpModal
            open={isRecordsHelpOpen}
            onClose={() => setIsRecordsHelpOpen(false)}
            title={page.records?.[0]?.title || 'Help'}
            infoText={page.records?.[0]?.info || ''}
            maxWidth="sm"
        />

        {/* Breach Help Modal */}
        <HelpModal
            open={isBreachHelpOpen}
            onClose={() => setIsBreachHelpOpen(false)}
            title={page.breach?.[0]?.title || 'Help'}
            infoText={page.breach?.[0]?.info || ''}
            maxWidth="sm"
        />

        {/* PDF Report Modal */}
        <PdfReportModal
            open={isPdfReportModalOpen}
            onClose={() => {
                setIsPdfReportModalOpen(false);
                setAutoLaunchPdf(false);
            }}
            autoLaunch={autoLaunchPdf}
        />
    </>
  );
};

export default RiskCalc;
